import clsx from 'clsx';
import { highlight, languages } from 'prismjs';
import React, {
  ReactElement, Suspense,useEffect, useState
} from 'react';
import { FiPlus } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import Editor from 'react-simple-code-editor';
import { toast,ToastContainer } from 'react-toastify';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import { Loader } from '@/components/loader/loader';
import { useUpdateSourceVersion } from '@/hooks/queries/useSources';
import {
  Source,
  SOURCE_TYPES,
  SourceConfig,
  SourceCreateReq,
  SourceGetRes,
  SourcePreview,
  SourceVersionDeprecateReq
} from '@/types/evals';
import { parseParams } from '@/utils/searchParams';

import ModalSourceCreate from '../_modalSourceCreate/modalSourceCreate';
import { ModalSourceEdit } from '../_modalSourceEdit/ModalSourceEdit';
import styles from './page.module.scss';

type SearchParams = {
  sid: string;
  version: string;
};

function PreviewComponent( { preview }: { preview: SourcePreview | undefined } ) {
  return (
    <>
      <h3>Data Sample Preview</h3>
      <div className={ clsx( 'layout__col', 'pad', styles.preview ) }>
        {
          preview?.items.map( el => {
            return (
              <div className={ clsx( 'layout__row', 'pad', styles.data ) } key={ `preview-${ el.id }` }>
                <Editor
                  value={ JSON.stringify( el, undefined, 2 ) }
                  onValueChange={ () => null }
                  highlight={ ( code ) => highlight( code, languages.json, 'json' ) }
                  padding={ 10 }
                  readOnly
                  style={ {
                    fontFamily: '"Fira code", "Fira Mono", monospace',
                    fontSize: 12
                  } }
                />
              </div>
            );
          } )
        }
      </div>
    </>
  );
}

function EvaluationSourcePage() {
  /* DATA */
  const [ searchParams, setSearchParams ] = useSearchParams();
  const {
    sid: qSourceId = '',
    version: qVersion = ''
  } = parseParams<SearchParams>( searchParams );
  const updateSourceVersionMutation = useUpdateSourceVersion();

  const [ isLoading, setIsLoading ] = useState<boolean>( true );
  const [ previewCount, _setPreviewCount ] = useState<number>( 0 );
  const [ preview, setPreview ] = useState<SourcePreview>();
  const [ source, setSource ] = useState<Source>();
  const [ isDeprecating, setIsDeprecating ] = useState<boolean>( false );
  const [ showModalSource, setShowModalSource ] = useState<boolean>( false );
  const [ isCreatingNewVersion, setIsCreatingNewVersion ] = useState<boolean>( false );

  /* HOOKS */
  useEffect( () => {
    if ( qSourceId ) _fetchData();
  }, [ qSourceId, qVersion ] );

  if ( !qSourceId ) {
    return (
      <div className='page'>
        <div>
          No source specified
        </div>
      </div>
    );
  }

  /* HELPERS */
  const _fetchData = async( explicitVersion?: number | 'latest' ) => {
    setIsLoading( true );
    try {
      const version = explicitVersion ?? ( qVersion === 'latest' ? 'latest' : parseInt( qVersion ) );
      const source: SourceGetRes = await APIGateway.evals.sourceGet( qSourceId, version );
      setSource( source );

      const preview = await APIGateway.evals.sourcePreviewGet( qSourceId, previewCount );
      setPreview( preview );
    } catch( e ) {
      console.error( e );
      toast( 'unable to get source' );
    } finally {
      setIsLoading( false );
    }
  };

  const _buildDoms = ( obj?: object, parentKey = '' ): ReactElement[] => {
    if( !obj ) return [ <></> ];

    return Object.entries( obj ).map( ( [ key, value ] ) => {
      const fullKey = parentKey ? `${ parentKey }.${ key }` : key;

      if ( typeof value === 'object' && value !== null ) {
        return _buildDoms( value, fullKey );
      }

      return (
        <div className={ clsx( 'layout__col', styles.config_item ) } key={ `source-key-${ fullKey }` }>
          <fieldset>
            <legend className={ styles.key }>{ fullKey }</legend>
            <p className={ styles.val }>{ value !== undefined && value !== null ? value.toString() : '-' }</p>
          </fieldset>
        </div>
      );
    } ).flat();
  };

  const _setDeprecation = async( isDeprecated: boolean ) => {
    if( !source ) return;
    setIsDeprecating( true );

    try {
      const depReq: SourceVersionDeprecateReq = { is_deprecated: isDeprecated };
      const source = await APIGateway.evals.sourceVersionDeprecate( qSourceId, Number( qVersion ), depReq );
      setSource( source );

      toast( `source version ${ isDeprecated ? 'deprecated' : 'undeprecated' }` );
    } catch( e ) {
      console.error( e );
      toast( 'unable to update deprecation status' );
    } finally {
      setIsDeprecating( false );
    }
  };

  const _updateVersion = ( newVersion: number ) => {
    const params = new URLSearchParams( searchParams.toString() );
    params.set( 'version', newVersion.toString() );
    setSearchParams( params );
  };


  /* HANDLERS */
  const onCreateNewSourceVersion = async( args: SourceCreateReq ) => {
    if( !source ) return;
    setIsCreatingNewVersion( true );

    try {
      const res = await updateSourceVersionMutation.mutateAsync( {
        sourceId: source.id,
        data: {
          config: args.config as SourceConfig
        }
      } );

      setShowModalSource( false );

      // Fetch the new version data first
      await _fetchData( res.version );

      // Update URL to match what we're now displaying
      _updateVersion( res.version );

      toast( `New source version (${ res.version }) created` );
    } catch( e ) {
      console.error( e );
      const errorMessage = e instanceof Error ? e.message : 'Unable to create new source version';
      toast( errorMessage );
    } finally {
      setIsCreatingNewVersion( false );
    }
  };

  const onSourceEditSuccess = async( newVersion?: number ) => {
    // Refresh the source data after successful edit
    if ( newVersion ) {
      await _fetchData( newVersion );
      _updateVersion( newVersion );
    } else {
      _fetchData();
    }
  };


  const onChangeDisplayedSourceVersion = async( version: number ) => {
    _updateVersion( version );
  };

  /* RENDERS */
  const renderInfo = (): ReactElement => (
    <>
      <span className={ clsx( styles.info_page ) }>
        <p>Source</p>
      </span>
      <span className={ styles.info_name }>
        <p>{ source?.name }</p>
      </span>
      <div className={ clsx( 'layout__row', styles.deprecate ) }>
        <span>Deprecated</span>
        <input
          checked={ source?.is_deprecated }
          disabled={ isDeprecating }
          onChange={ e => _setDeprecation( e.target.checked ) }
          type='checkbox'
        />
      </div>
      <div className={ clsx( 'layout__row', 'space_none', styles.info_version ) }>
        <p>Version</p>
        <select value={ source?.version } onChange={ ( e ) => { onChangeDisplayedSourceVersion( parseInt( e.target.value ) ); } }>
          {
            Array.from( { length: source?.latest_version_number ?? 0 }, ( _, i ) => i + 1 ).map( ( version ) => (
              <option value={ version } key={ `version-option-${ version }` }>{ version }</option>
            ) )
          }
        </select>
      </div>
      <div className={ styles.info_actions }>
        <button className='create_button' onClick={ () => setShowModalSource( ( prev ) => !prev ) } disabled={ isCreatingNewVersion }>
          <FiPlus />{ isCreatingNewVersion ? 'Creating...' : 'New Version' }
        </button>
      </div>
    </>
  );

  const renderSource = (): ReactElement => {
    if ( !source ) return <></>;
    const config = { ...( source?.config ) };

    return (
      <>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <div className={ clsx( 'layout__row', 'pad' ) }>
            <h4>General Properties</h4>
          </div>
          <div className={ clsx( 'layout__col', styles.source_pane ) }>
            {
              _buildDoms( { ...source, config: undefined } )
            }
          </div>
        </div>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <div className={ clsx( 'layout__row', 'pad' ) }>
            <h4>Config Properties</h4>
          </div>
          <div className={ clsx( 'layout__col', styles.source_pane ) }>
            {
              _buildDoms( config )
            }
          </div>
        </div>
      </>
    );
  };

  return (
    <div className='page'>
      <ToastContainer />
      {
        isLoading
          ? <Loader />
          : (
              <>
                {
                  showModalSource && source && (
                    source.source_type === SOURCE_TYPES.JSON ? (
                      <ModalSourceEdit
                        source={ source }
                        onClose={ () => setShowModalSource( false ) }
                        onSuccess={ onSourceEditSuccess }
                      />
                    ) : (
                      <ModalSourceCreate
                        cbAction={ onCreateNewSourceVersion }
                        onCloseAction={ () => setShowModalSource( !showModalSource ) }
                        sourceToEdit={ source }
                        isPending={ isCreatingNewVersion }
                      />
                    )
                  )
                }
                <div className={ clsx( 'layout__row', styles.layout__info ) }>
                  { renderInfo() }
                </div>
                <div className={ clsx( 'layout__row', 'pad', styles.layout__source ) }>
                  { renderSource() }
                </div>
                <div className={ clsx( 'layout__col', 'pad', styles.layout__preview ) }>
                  <PreviewComponent preview={ preview } />
                </div>
              </>
            )
      }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <EvaluationSourcePage />
    </Suspense>
  );
}
